import {
  resolveProductLayout,
  type QuoteWizardFlow,
} from '@/app/config/products.config';
import { BrokerWriteGate } from '@/features/brokers/components/broker-write-gate.component';
import { useSelectedBrokerWriteGuard } from '@/features/brokers/hooks';
import { QuoteParamsProvider } from '@/features/quotes/context/quote-params.context';
import { requirePermissions } from '@/shared/utils';
import { createFileRoute, redirect } from '@tanstack/react-router';

export const Route = createFileRoute('/_app/quotes/products/new')({
  component: NewQuoteLayoutRoute,
  beforeLoad: async () => {
    const hasPermission = await requirePermissions(['policies.quotes.write']);
    if (!hasPermission) {
      throw redirect({ to: '/dashboard' });
    }
  },
  validateSearch: (search: Record<string, unknown>) => {
    const rawFlow = search.quoteFlow as string | undefined;
    const quoteFlow: QuoteWizardFlow =
      rawFlow === 'edit' ? 'edit' : rawFlow === 'view' ? 'view' : 'new';
    return {
      productId: (search.productId as string) || '',
      category: (search.category as string) || '',
      productCode: (search.productCode as string) || '',
      /** Código da etapa do wizard (ex.: CLIENT_INFO); alinhado ao schema do BFF */
      stepCode: (search.stepCode as string) || '',
      quoteFlow,
      // Spread condicional → `quotationVersionId` fica OPCIONAL no schema de
      // busca (não quebra as navegações existentes que não o enviam). Quando
      // presente, o wizard hidrata o snapshot daquela versão InsureMO (tela de
      // resumo de uma versão específica) em vez da cotação atual.
      ...(typeof search.quotationVersionId === 'string' &&
      search.quotationVersionId
        ? { quotationVersionId: search.quotationVersionId }
        : {}),
    };
  },
});

function NewQuoteLayoutRoute() {
  const search = Route.useSearch();
  const Layout = resolveProductLayout(search.productCode);
  const quoteWriteGuard = useSelectedBrokerWriteGuard('quote');
  // Modo somente leitura não escreve nada: não trava.
  const guard =
    search.quoteFlow === 'view' ? { blocked: false } : quoteWriteGuard;

  return (
    <BrokerWriteGate guard={guard} backTo='/quotes'>
      <QuoteParamsProvider>
        <Layout
          productId={search.productId}
          category={search.category}
          productCode={search.productCode}
          stepCode={search.stepCode}
          quoteFlow={search.quoteFlow}
        />
      </QuoteParamsProvider>
    </BrokerWriteGate>
  );
}
